import { ConversationParticipantView } from '@mark43/cad-api';
import { buildParticipantKey } from '../store/helpers';
import { ParticipantIdentities } from '../store/types';
import { ActiveFilterTabMenu, FilterTabMenuType, ConversationsFilter } from '../types';

// getConversationsFilterList returns all the filters from the filter bar in a flattened list so that the
// hotkey next/previous filter can determine which filter to select next
export const getConversationsFilterList = (participantIdentities?: ParticipantIdentities) => {
    const conversationsFilterList: ConversationsFilter[] = [];
    if (participantIdentities === undefined) {
        return conversationsFilterList;
    }
    conversationsFilterList.push({ filterType: 'ALL_ACTIVE', filterParticipant: null });
    conversationsFilterList.push({
        filterType: 'USER',
        filterParticipant: participantIdentities.activeMappedByType.USER,
    });
    if (participantIdentities.activeMappedByType.UNIT) {
        conversationsFilterList.push({
            filterType: 'UNIT',
            filterParticipant: participantIdentities.activeMappedByType.UNIT,
        });
    }
    if (participantIdentities.activeMappedByType.CALL_TAKER_STATION) {
        conversationsFilterList.push({
            filterType: 'CALL_TAKER_STATION',
            filterParticipant: participantIdentities.activeMappedByType.CALL_TAKER_STATION,
        });
    }
    participantIdentities.activeMappedByType.DISPATCH_AREA?.forEach((participantIdentity) => {
        conversationsFilterList.push({
            filterType: 'DISPATCH_AREA',
            filterParticipant: participantIdentity,
        });
    });
    participantIdentities.inactive?.forEach((participantIdentity) => {
        conversationsFilterList.push({
            filterType: 'INACTIVE',
            filterParticipant: participantIdentity,
        });
    });
    return conversationsFilterList;
};

// returns all the user's participant identities associated to the specified conversation filter
export const getFilterParticipants = (
    conversationFilter: ConversationsFilter,
    participantIdentities?: ParticipantIdentities
) => {
    const { filterType, filterParticipant } = conversationFilter;
    if (participantIdentities === undefined || filterParticipant === undefined) {
        return [];
    } else if (filterType === 'ALL_ACTIVE') {
        return participantIdentities.active || [];
    } else if (filterType === 'INACTIVE') {
        return [filterParticipant];
    } else if (
        participantIdentities.activeMappedByKey[
            buildParticipantKey(filterParticipant.id, filterParticipant.entityType)
        ]
    ) {
        return [
            participantIdentities.activeMappedByKey[
                buildParticipantKey(filterParticipant.id, filterParticipant.entityType)
            ],
        ];
    }
    return [];
};

// return the index of the currently selected conversation filter from of a flat list of all the conversation filters
export const getSelectedConversationsFilterIndex = (
    conversationsFilterList: ConversationsFilter[],
    selectedConversationFilter?: ConversationsFilter
) => {
    return conversationsFilterList.findIndex(
        (conversationsFilter) =>
            conversationsFilter.filterType === selectedConversationFilter?.filterType &&
            participantKeysMatch(
                conversationsFilter.filterParticipant,
                selectedConversationFilter.filterParticipant
            )
    );
};

export function participantKeysMatch(
    participant1: ConversationParticipantView | null,
    participant2: ConversationParticipantView | null
) {
    return (
        participant1 &&
        participant2 &&
        buildParticipantKey(participant1.id, participant1.entityType) ===
            buildParticipantKey(participant2.id, participant2.entityType)
    );
}

export const getConversationFilterFromParticipantIdentity = (
    participantIdentity: ConversationParticipantView
) => {
    return {
        filterType: participantIdentity.entityType,
        filterParticipant: participantIdentity,
    };
};

export const isFilterTabMenu = (filterType?: ConversationsFilter['filterType']): boolean =>
    !!filterType && ['DISPATCH_AREA', 'INACTIVE'].includes(filterType);

/**
 * close or clear activeFilterTabMenu.
 * if filterTabMenu (DISPATCH_AREAS or INACTIVE) is selected, set activeFilterTabMenu.isOpen to false, which will still keep current filterTabGroup as selected/active, just close the menu.
 * if other non-menu tab is clicked, clear the selected/active filterTabMenu so that no active filterTabMenu is set for holding ctrl key.
 */
export const closeAndClearActiveFilterTabMenu = ({
    filterType,
    setActiveFilterTabMenu: setActiveFilterTabMenu,
}: {
    filterType?: ConversationsFilter['filterType'];
    setActiveFilterTabMenu?: (activeFilterTabMenu?: ActiveFilterTabMenu | undefined) => void;
}) => {
    if (setActiveFilterTabMenu) {
        if (isFilterTabMenu(filterType)) {
            setActiveFilterTabMenu({ tab: filterType as FilterTabMenuType, isOpen: false });
        } else {
            setActiveFilterTabMenu(undefined);
        }
    }
};
